import { z } from 'zod'
import { Track, ProgramCategory } from '../enums/index'

// --- Programs ---

export const programSchema = z.object({
  code: z
    .string()
    .min(2, 'Le code doit contenir au moins 2 caractères')
    .max(128, 'Le code ne doit pas dépasser 128 caractères')
    .regex(
      /^[A-Z0-9-_]+$/,
      'Le code doit contenir uniquement des majuscules, chiffres, tirets et underscores'
    ),
  title: z
    .string()
    .min(3, 'Le titre doit contenir au moins 3 caractères')
    .max(255, 'Le titre ne doit pas dépasser 255 caractères'),
  summary: z.string().optional(),
  subjectId: z.string().uuid('La matière doit être un UUID valide'),
  levelId: z.string().uuid('Le niveau doit être un UUID valide'),
  track: z.nativeEnum(Track),
  category: z.nativeEnum(ProgramCategory),
})

export type ProgramFormData = z.infer<typeof programSchema>

// --- Competencies ---

export const competencySchema = z.object({
  code: z
    .string()
    .min(2, 'Le code est requis')
    .max(128, 'Le code ne doit pas dépasser 128 caractères')
    .regex(
      /^[A-Z0-9-_]+$/,
      'Le code doit contenir uniquement des majuscules, chiffres, tirets et underscores'
    ),
  statement: z
    .string()
    .min(5, "L'énoncé est requis")
    .max(512, "L'énoncé ne doit pas dépasser 512 caractères"),
  description: z.string().optional(),
  domain: z
    .string()
    .max(128, 'Le domaine ne doit pas dépasser 128 caractères')
    .optional(),
  subjectId: z.string().uuid('La matière doit être un UUID valide'),
  // slug est généré automatiquement dans le service
})

export type CompetencyFormData = z.infer<typeof competencySchema>

// --- Concepts ---

export const conceptSchema = z.object({
  code: z
    .string()
    .min(2, 'Le code est requis')
    .max(128, 'Le code ne doit pas dépasser 128 caractères')
    .regex(
      /^[A-Z0-9-_]+$/,
      'Le code doit contenir uniquement des majuscules, chiffres, tirets et underscores'
    ),
  label: z
    .string()
    .min(2, 'Le libellé est requis')
    .max(255, 'Le libellé ne doit pas dépasser 255 caractères'),
  description: z.string().optional(),
  synonyms: z.string().optional(), // Sera converti en tableau
  subjectId: z.string().uuid('La matière doit être un UUID valide'),
  // slug est généré automatiquement dans le service
})

export type ConceptFormData = z.infer<typeof conceptSchema>

// --- Subjects ---

export const subjectSchema = z.object({
  code: z
    .string()
    .min(2, 'Le code est requis')
    .max(64, 'Le code ne doit pas dépasser 64 caractères')
    .regex(
      /^[A-Z_]+$/,
      'Le code doit contenir uniquement des majuscules et underscores'
    ),
  label: z
    .string()
    .min(2, 'Le libellé est requis')
    .max(255, 'Le libellé ne doit pas dépasser 255 caractères'),
  description: z.string().optional(),
  color: z
    .string()
    .regex(/^#[0-9A-F]{6}$/i, 'Code couleur invalide (ex: #FF0000)')
    .optional()
    .or(z.literal('')),
  typeId: z.string().uuid('Le type de matière doit être un UUID valide'),
  officialRefCode: z.string().optional(),
  // slug est généré automatiquement dans le service
})

export type SubjectFormData = z.infer<typeof subjectSchema>

// --- Levels ---

export const levelSchema = z.object({
  code: z
    .string()
    .min(2, 'Le code est requis')
    .max(64, 'Le code ne doit pas dépasser 64 caractères')
    .regex(
      /^[A-Z0-9_]+$/,
      'Le code doit contenir uniquement des majuscules, chiffres et underscores'
    ),
  label: z
    .string()
    .min(2, 'Le libellé est requis')
    .max(255, 'Le libellé ne doit pas dépasser 255 caractères'),
  description: z.string().optional(),
  order: z.number().int().min(0, "L'ordre doit être un entier positif ou nul"),
  track: z
    .array(z.nativeEnum(Track))
    .min(1, 'Au moins une voie doit être sélectionnée'),
})

export type LevelFormData = z.infer<typeof levelSchema>
